import React, { useState, useEffect } from 'react'
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import stylesheet from '../detail-container.stylesheet'
import Helper from '../../../../utils/helper'
import { PatientService } from '../../../../services/patient.service'
import { formatDisplayName } from '../../../../utils/display-name-formatter'
import DialogTitleHeaderComponent from './dialog-title-header.component'
import {
  experimentService, ExperimentTemplate, ExperimentTemplateReadout, Experiment, ExperimentReadout,
} from '../../../../services/experiment.service'

interface IProps {
  open: boolean
  handleClose: any
  onSaved: any
  refId: string
  templates: ExperimentTemplate[]
  medications: { id: number; name: string }[]
  patientId: number | string
  experiment?: Experiment | null   // present => read-only view; absent => authoring
  classes: ClassNameMap
}

interface EditableReadout extends ExperimentTemplateReadout {
  kept: boolean; added?: boolean; recheckDays: string
}

const subjectOf = (r: ExperimentTemplateReadout) => r.measurement_name || r.questionnaire_name || ''
const markerName = (r: { display_name?: string | null; measurement_name: string | null; questionnaire_name: string | null }) =>
  r.display_name || r.measurement_name || r.questionnaire_name || ''
const CHANGE_LABEL: Record<string, string> = { observe: 'Observation', add: 'Started', modify_dose: 'Dose change', remove: 'Stopped' }
const STATUS_LABEL: Record<string, string> = { proposed: 'Proposed', active: 'Active', closed: 'Closed', canceled: 'Canceled' }

/**
 * The lifecycle moves offered on a plan, mirroring the server rule
 * (EXPERIMENT_TRANSITIONS, ADR D17) so the UI cannot offer a move that answers 409.
 * Each hint states what the move does to the PATIENT's surfaces (ADR D19) — that is
 * the consequence the provider is actually choosing:
 *
 *   proposed → Activate   starts the patient's symptom check-ins
 *   active   → Close      shut off after the readouts are in; check-ins stop
 *   active   → Cancel     abandon a running plan
 *
 * Terminal statuses offer nothing. None of these DELETE — the record and readouts
 * are kept, which is the whole reason the endpoint exists instead of reusing delete.
 */
const LIFECYCLE_MOVES: Record<string, { status: 'active' | 'closed' | 'canceled'; label: string; hint: string }[]> = {
  proposed: [
    { status: 'active', label: 'Activate', hint: "Start this plan: its symptoms begin charting on the patient's record and appear as check-ins they can answer." },
    { status: 'canceled', label: 'Cancel', hint: 'Abandon this plan before it starts. The record is kept, it simply never runs.' },
  ],
  active: [
    { status: 'closed', label: 'Close', hint: 'Shut this plan off — its symptoms stop charting and its check-ins stop being asked. Readouts and history are kept.' },
    { status: 'canceled', label: 'Cancel', hint: 'Abandon this running plan. Same shut-off, recorded as abandoned rather than read out.' },
  ],
  closed: [],
  canceled: [],
}
const roleLabel = (r: ExperimentReadout) =>
  r.role === 'modifier' ? 'modifier' : r.expected_direction === 'stable' ? 'safety' : (r.rank || 'readout')
const expectedNarrative = (r: ExperimentReadout) => {
  if (r.role === 'modifier') return 'predicts the primary response'
  if (r.expected_direction === 'stable') {
    const f = r.finding_direction === 'increase' ? 'a rise is the finding'
      : r.finding_direction === 'decrease' ? 'a fall is the finding' : ''
    return `no change expected${f ? `; ${f}` : ''}`
  }
  return r.expected_direction ? `expected to ${r.expected_direction}` : 'direction expected'
}
const recheckInDays = (r: ExperimentReadout, encounter?: number): number | null =>
  r.recheck_at && encounter ? Math.round((r.recheck_at - encounter) / 86400) : null

const ExperimentDialogComponent: React.FC<IProps> = ({
  open, handleClose, onSaved, refId, templates, medications, patientId, experiment, classes,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [readouts, setReadouts] = useState<EditableReadout[]>([])
  const [editRO, setEditRO] = useState<{ name: string; display: string; kind: 'measurement' | 'questionnaire' }[]>([])
  const [measurements, setMeasurements] = useState<any[]>([])
  // Wide trackable-symptom options ({name, display_name, category}) from /tox-questionnaires.
  const [symptomOptions, setSymptomOptions] = useState<any[]>([])
  const [medicationId, setMedicationId] = useState('')
  const [saving, setSaving] = useState(false)
  const [moving, setMoving] = useState(false)   // a lifecycle transition in flight (D20)

  // Reuse the Add-measurements search source (the KB measurement list) — both modes.
  useEffect(() => {
    if (open && patientId) {
      new PatientService().getMeasurementReport(String(patientId))
        .then((res: any) => setMeasurements(
          (res?.data || []).slice().sort((a: any, b: any) =>
            formatDisplayName(a.display_name || a.name).toLowerCase()
              .localeCompare(formatDisplayName(b.display_name || b.name).toLowerCase()))))
        .catch(() => { /* non-fatal */ })
    }
  }, [open, patientId])

  // Editing an existing experiment: seed the marker set from its readouts.
  useEffect(() => {
    if (experiment) {
      setEditRO((experiment.readouts || []).map((r) => ({
        name: r.measurement_name || r.questionnaire_name || '', display: markerName(r),
        kind: (r.measurement_name ? 'measurement' : 'questionnaire') as 'measurement' | 'questionnaire',
      })))
    }
  }, [experiment])

  const onPickTemplate = (name: string) => {
    setSelectedTemplate(name)
    const tpl = templates.find((t) => t.name === name)
    setReadouts((tpl?.readouts || []).map((r) => ({
      ...r, kept: true, recheckDays: r.default_recheck_days != null ? String(r.default_recheck_days) : '',
    })))
  }
  const toggleKeep = (subject: string) =>
    setReadouts((rs) => rs.map((r) => (subjectOf(r) === subject ? { ...r, kept: !r.kept } : r)))
  const setRecheck = (subject: string, days: string) =>
    setReadouts((rs) => rs.map((r) => (subjectOf(r) === subject ? { ...r, recheckDays: days.replace(/[^0-9]/g, '') } : r)))
  const addMeasurement = (m: any) => {
    const name = m?.name
    if (!name) return
    if (experiment) {
      if (editRO.some((r) => r.name === name)) return
      setEditRO((rs) => [...rs, { name, display: formatDisplayName(m.display_name || m.name), kind: 'measurement' }])
    } else {
      if (readouts.some((r) => subjectOf(r) === name)) return
      setReadouts((rs) => [...rs, {
        measurement_name: name, questionnaire_name: null,
        display_name: formatDisplayName(m.display_name || m.name),
        role: 'readout', rank: null, expectation: 'directional', default_recheck_days: null,
        declared_by: null, kept: true, added: true, recheckDays: '',
      }])
    }
  }

  // Symptom (questionnaire) readouts — the picker is the WIDE trackable set
  // (tox ∪ *__attr questionnaires), the SAME menu as the à-la-carte "+ track a
  // symptom" affordance, so it reaches questionnaire-only entries the symptom-concept
  // namespace misses (e.g. myopathy). Adding a questionnaire readout also bridges it
  // to a tracked calc server-side (experiment_api add_readouts) → collectible + charts.
  const searchSymptoms = (term: string) => {
    const t = term.trim()
    if (!t) { setSymptomOptions([]); return }
    new PatientService().getTrackablePicker(refId, t)
      .then((res: any) => setSymptomOptions(res.data || []))
      .catch(() => { /* non-fatal */ })
  }
  const addSymptom = (c: any) => {
    if (!c) return
    const qn = c.questionnaire_name || c.name
    const disp = c.display_name || qn
    if (experiment) {
      if (editRO.some((r) => r.name === qn)) return
      setEditRO((rs) => [...rs, { name: qn, display: disp, kind: 'questionnaire' }])
    } else {
      if (readouts.some((r) => subjectOf(r) === qn)) return
      setReadouts((rs) => [...rs, {
        measurement_name: null, questionnaire_name: qn, display_name: disp,
        role: 'readout', rank: null, expectation: 'watched', default_recheck_days: null,
        declared_by: null, kept: true, added: true, recheckDays: '',
      }])
    }
  }

  const removeViewMarker = (name: string) => setEditRO((rs) => rs.filter((r) => r.name !== name))

  /** Activate / Close / Cancel (ADR D17/D20). The server's returned status is
   *  authoritative; onSaved() re-fetches so the card's status chip and the record's
   *  charted symptoms both follow in one refresh. */
  const moveStatus = async (status: 'active' | 'closed' | 'canceled') => {
    if (!experiment) return
    setMoving(true)
    try {
      await experimentService.transitionStatus(refId, experiment.id, status)
      onSaved()
    } catch (e) {
      toast.error(Helper.getErrorText(e), Helper.bottom_center())
    } finally { setMoving(false) }
  }

  const saveEdit = async () => {
    if (!experiment) return
    const orig = new Set((experiment.readouts || []).map((r) => r.measurement_name || r.questionnaire_name || ''))
    const cur = new Set(editRO.map((r) => r.name))
    const add_readouts = editRO.filter((r) => !orig.has(r.name)).map((r) =>
      (r.kind === 'questionnaire' ? { questionnaire_name: r.name } : { measurement_name: r.name }))
    const remove_subjects = Array.from(orig).filter((n) => !cur.has(n))
    if (!add_readouts.length && !remove_subjects.length) { handleClose(); return }
    setSaving(true)
    try {
      await experimentService.updateReadouts(refId, experiment.id, { add_readouts, remove_subjects })
      onSaved()
    } catch (e) {
      toast.error('Could not update the intervention.', Helper.bottom_center())
    } finally { setSaving(false) }
  }

  const save = async () => {
    const tpl = templates.find((t) => t.name === selectedTemplate)
    if (!tpl) return
    const templateSubjects = new Set((tpl.readouts || []).map(subjectOf))
    const remove_subjects = readouts.filter((r) => !r.kept && templateSubjects.has(subjectOf(r))).map(subjectOf)
    const add_readouts = readouts.filter((r) => r.kept && r.added).map((r) =>
      (r.questionnaire_name ? { questionnaire_name: r.questionnaire_name } : { measurement_name: r.measurement_name || undefined }))
    const now = Math.floor(Date.now() / 1000)
    const readout_overrides: Record<string, unknown> = {}
    readouts.forEach((r) => {
      if (!r.kept || r.role === 'modifier') return
      const days = parseInt(r.recheckDays, 10)
      const def = r.default_recheck_days != null ? String(r.default_recheck_days) : ''
      if (!isNaN(days) && String(days) !== def) readout_overrides[subjectOf(r)] = { recheck_at: now + days * 86400 }
    })
    setSaving(true)
    try {
      await experimentService.createExperiment(refId, {
        template_name: selectedTemplate,
        medication_id: medicationId ? Number(medicationId) : null,
        remove_subjects, add_readouts,
        ...(Object.keys(readout_overrides).length ? { readout_overrides } : {}),
      })
      onSaved()
    } catch (e) {
      toast.error('Could not save the intervention.', Helper.bottom_center())
    } finally { setSaving(false) }
  }

  const title = experiment ? experiment.name : 'New intervention'

  // Symptom picker — server-side search over the concept namespace (999 concepts),
  // so we never client-filter; already-tracked subjects are disabled.
  const symptomField = (selected: Set<string>) => (
    <Autocomplete
      size="small"
      options={symptomOptions}
      value={null}
      filterOptions={(x) => x}
      getOptionLabel={(o: any) => o?.display_name || o?.name || ''}
      getOptionDisabled={(o: any) => selected.has(o?.questionnaire_name || o?.name)}
      isOptionEqualToValue={(o: any, v: any) => o?.name === v?.name}
      onInputChange={(_e, val, reason) => { if (reason === 'input') searchSymptoms(val) }}
      onChange={(_e, v) => addSymptom(v)}
      renderOption={(props, o: any) => (
        <li {...props} key={o.name}>
          <span>{o.display_name || o.name}</span>
          {!o.has_questionnaire && (
            <span style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.7rem' }}>· no instrument</span>
          )}
        </li>
      )}
      renderInput={(params) => <TextField {...params} size="small" variant="standard" label="Add a symptom (questionnaire)" />}
    />
  )

  return (
    // Font scale: this dialog carried almost no sizing of its own, so marker names,
    // field labels and inputs rendered at MUI's defaults and read much larger than
    // the rest of the app, which runs at 0.65-0.8rem (RD 2026-08-08).
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiTypography-root': { fontSize: '0.75rem' },
        '& .MuiInputBase-input': { fontSize: '0.75rem' },
        '& .MuiInputLabel-root': { fontSize: '0.75rem' },
        '& .MuiFormLabel-root': { fontSize: '0.75rem' },
        '& .MuiChip-root': { fontSize: '0.62rem', height: 18 },
        '& .MuiButton-root': { fontSize: '0.7rem' },
        '& .MuiMenuItem-root': { fontSize: '0.75rem' },
      }}
    >
      <DialogTitle sx={{ py: 1 }}>
        <DialogTitleHeaderComponent title={title} handleDialogClose={handleClose} />
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ py: 1.5 }}>
        {experiment ? (
          /* Read-only view */
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: 92 }}>Intervention</Typography>
              <Typography variant="subtitle2">
                {experiment.perturbation_label || 'Observation — no intervention'}
                {experiment.perturbation_label && ` · ${CHANGE_LABEL[experiment.change] || experiment.change}`}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: 92 }}>Status</Typography>
              <Typography variant="subtitle2">{STATUS_LABEL[experiment.status] || experiment.status}</Typography>
              {/* Lifecycle (ADR D17/D20). Activate starts the patient's symptom check-ins;
                  Close/Cancel shut them off. Neither deletes the record — the readouts and
                  history are kept, which is why this is not the delete icon. */}
              {LIFECYCLE_MOVES[experiment.status]?.map((mv) => (
                <Button
                  key={mv.status}
                  size="small"
                  variant={mv.status === 'active' ? 'contained' : 'outlined'}
                  disabled={moving}
                  title={mv.hint}
                  onClick={() => void moveStatus(mv.status)}
                >
                  {moving ? '…' : mv.label}
                </Button>
              ))}
            </Stack>
            <Typography variant="caption" color="textSecondary">
              {experiment.status === 'proposed'
                ? 'Not running yet — no symptom check-ins are being asked of the patient.'
                : experiment.status === 'active'
                ? 'Running — symptoms chart on the record and check-ins are being asked.'
                : 'Retired — its symptoms no longer chart and no check-ins are asked.'}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 0.5 }}>Markers (expected change)</Typography>
            {editRO.map((er) => {
              const r = (experiment.readouts || []).find((x) => (x.measurement_name || x.questionnaire_name) === er.name)
              const days = r ? recheckInDays(r, experiment.encounter_date) : null
              return (
                <Stack key={er.name} direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="subtitle2" sx={{ minWidth: 150 }}>{er.display}</Typography>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ flexGrow: 1 }}>
                    {r ? `${roleLabel(r)} · ${expectedNarrative(r)}` : 'new — resolves on save'}
                  </Typography>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ minWidth: 62 }}>
                    {days != null ? `recheck ${days}d` : ''}
                  </Typography>
                  <Button size="small" onClick={() => removeViewMarker(er.name)} sx={{ minWidth: 28, px: 0.5 }}>×</Button>
                </Stack>
              )
            })}
            <Autocomplete
              size="small"
              options={measurements}
              value={null}
              getOptionLabel={(o: any) => formatDisplayName(o?.display_name || o?.name) || ''}
              isOptionEqualToValue={(o: any, v: any) => o?.name === v?.name}
              filterOptions={(opts, state) => {
                const selected = new Set(editRO.map((r) => r.name))
                const term = state.inputValue.toLowerCase().trim()
                return opts.filter((o: any) => !selected.has(o?.name) &&
                  (!term || formatDisplayName(o?.display_name || o?.name).toLowerCase().includes(term)))
              }}
              onChange={(_e, v) => addMeasurement(v)}
              renderInput={(params) => <TextField {...params} size="small" variant="standard" label="Add a measurement" />}
            />
            {symptomField(new Set(editRO.map((r) => r.name)))}
          </Stack>
        ) : (
          /* Authoring */
          <Stack spacing={1.5}>
            <FormControl size="small" fullWidth>
              <InputLabel id="ed-tpl">Template</InputLabel>
              <Select labelId="ed-tpl" label="Template" value={selectedTemplate} onChange={(e) => onPickTemplate(e.target.value)}>
                {templates.map((t) => (<MenuItem key={t.name} value={t.name} sx={{ fontSize: '0.8rem' }}>{t.display_name || t.name}</MenuItem>))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="ed-pert">Intervention (from care plan)</InputLabel>
              <Select labelId="ed-pert" label="Intervention (from care plan)" value={medicationId} onChange={(e) => setMedicationId(e.target.value)}>
                <MenuItem value=""><em>None — observe</em></MenuItem>
                {medications.map((m) => (<MenuItem key={m.id} value={String(m.id)} sx={{ fontSize: '0.8rem' }}>{m.name}</MenuItem>))}
              </Select>
            </FormControl>
            {selectedTemplate && (
              <>
                <Typography variant="caption" color="textSecondary">
                  Markers — untick to suppress (display-only), edit the recheck cadence, or add a measurement.
                </Typography>
                {/* Column heading once, rather than a floating "recheck d" label
                    repeated on every row. The per-marker inputs stay — the cadences
                    genuinely differ (prealbumin 28d vs troponin 180d) and a single
                    shared value would flatten that. */}
                <Stack direction="row" spacing={0.75} alignItems="flex-end" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="textSecondary" sx={{ width: 56, textAlign: 'left' }}>
                    recheck (days)
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  {readouts.map((r) => {
                    const subject = subjectOf(r); const isMod = r.role === 'modifier'
                    return (
                      <Stack key={subject} direction="row" spacing={0.75} alignItems="center">
                        <Button size="small" variant={r.kept ? 'contained' : 'outlined'} onClick={() => toggleKeep(subject)}
                          sx={{ minWidth: 76, fontSize: '0.65rem', py: 0.1 }}>
                          {r.kept ? 'watching' : 'suppressed'}
                        </Button>
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{markerName(r)}</Typography>
                        {/* variant="standard" at 0.7rem matches how the rest of the app
                            takes numeric entry (cf. measurement-card). The outlined box
                            with a floating label was the styling that stood out. */}
                        {!isMod && r.kept && (
                          <TextField size="small" variant="standard" value={r.recheckDays}
                            onChange={(e) => setRecheck(subject, e.target.value)}
                            sx={{ width: 56 }}
                            inputProps={{ inputMode: 'numeric', style: { fontSize: '0.7rem' } }} />
                        )}
                        {/* Keep the column aligned when a modifier row has no input. */}
                        {(isMod || !r.kept) && <Box sx={{ width: 56 }} />}
                      </Stack>
                    )
                  })}
                </Stack>
                <Autocomplete
                  size="small"
                  options={measurements}
                  value={null}
                  getOptionLabel={(o: any) => formatDisplayName(o?.display_name || o?.name) || ''}
                  isOptionEqualToValue={(o: any, v: any) => o?.name === v?.name}
                  filterOptions={(opts, state) => {
                    const selected = new Set(readouts.map((r) => subjectOf(r)))
                    const term = state.inputValue.toLowerCase().trim()
                    return opts.filter((o: any) => !selected.has(o?.name) &&
                      (!term || formatDisplayName(o?.display_name || o?.name).toLowerCase().includes(term)))
                  }}
                  onChange={(_e, v) => addMeasurement(v)}
                  renderInput={(params) => <TextField {...params} size="small" variant="standard" label="Add a measurement" />}
                />
                {symptomField(new Set(readouts.map((r) => subjectOf(r))))}
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      {!experiment ? (
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={handleClose}>Cancel</Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving || !selectedTemplate}>
            {saving ? 'Saving…' : 'Create'}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={handleClose}>Close</Button>
          <Button size="small" variant="contained" onClick={saveEdit} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default withStyles(stylesheet)(ExperimentDialogComponent)
